import {
  ICreateOrUpdateSolutionPayload,
  ICreateOrUpdateWhiteboardSolutionPayload,
  IRunCodePayload,
  IgetWhiteboardSolutionByQuestionSlugAPIResponse,
  TCreateSolutionAPIResponse,
  ICreateOrUpdateSubjectiveSolutionPayload,
  IGetSubjectiveSolutionByQuestionSlugAPIResponse,
  IUploadAttachmentAPIResponse,
} from "@/types/solutions.types";
import { HTTP_METHODS, HTTP_STATUS_CODES } from "@/constants/HttpConstants";
import { sendRequestToInterviewWithCredential } from "@/utils/request.utils";
import { ISolution } from "@/types/questions.types";
import { IRunCodeAPIResponse } from "@/types/codingProblems.types";

export const getSolutionAPI = (interviewSlug: string, solutionSlug: string) =>
  sendRequestToInterviewWithCredential<ISolution>({
    method: HTTP_METHODS.GET,
    url: `/interviews/${interviewSlug}/solutions/${solutionSlug}/`,
  });

export const updateSolutionAPI = (
  interviewSlug: string,
  solutionSlug: string,
  payload: ICreateOrUpdateSolutionPayload,
) =>
  sendRequestToInterviewWithCredential({
    method: HTTP_METHODS.PUT,
    url: `/interviews/${interviewSlug}/solutions/${solutionSlug}/`,
    data: payload,
  });

export const createSolutionAPI = (
  interviewSlug: string,
  payload: ICreateOrUpdateSolutionPayload,
) =>
  sendRequestToInterviewWithCredential<TCreateSolutionAPIResponse>({
    method: HTTP_METHODS.POST,
    url: `/interviews/${interviewSlug}/solutions/`,
    data: payload,
  });

export const runCodeAPI = (
  interviewSlug: string,
  solutionSlug: string,
  payload: IRunCodePayload,
) =>
  sendRequestToInterviewWithCredential<IRunCodeAPIResponse>({
    method: HTTP_METHODS.POST,
    url: `/interviews/${interviewSlug}/solutions/${solutionSlug}/evaluate/`,
    data: payload,
  });

export const getWhiteboardSolutionAPI = (
  interviewSlug: string,
  solutionSlug: string,
) =>
  sendRequestToInterviewWithCredential({
    method: HTTP_METHODS.GET,
    url: `/interviews/${interviewSlug}/solution/${solutionSlug}/whiteboard/`,
  });

export const createOrUpdateWhiteboardSolutionAPI = (
  interviewSlug: string,
  payload: ICreateOrUpdateWhiteboardSolutionPayload,
) =>
  sendRequestToInterviewWithCredential({
    method: HTTP_METHODS.POST,
    url: `/interviews/${interviewSlug}/solutions/`,
    data: payload,
  });

export const getSolutionByQuestionSlugAPI = (
  interviewSlug: string,
  questionSlug: string,
) =>
  sendRequestToInterviewWithCredential<ISolution>(
    {
      method: HTTP_METHODS.GET,
      url: `/interviews/${interviewSlug}/questions/${questionSlug}/solution/`,
    },
    [HTTP_STATUS_CODES.NOT_FOUND],
  );

export const getWhiteboardSolutionByQuestionSlugAPI = (
  interviewSlug: string,
  slug: string,
) =>
  sendRequestToInterviewWithCredential<IgetWhiteboardSolutionByQuestionSlugAPIResponse>(
    {
      method: HTTP_METHODS.GET,
      url: `/interviews/${interviewSlug}/questions/${slug}/whiteboard/`,
    },
    [HTTP_STATUS_CODES.NOT_FOUND],
  );

export const getSubjectiveSolutionByQuestionSlugAPI = (
  interviewSlug: string,
  questionSlug: string,
) =>
  sendRequestToInterviewWithCredential<IGetSubjectiveSolutionByQuestionSlugAPIResponse>(
    {
      method: HTTP_METHODS.GET,
      url: `/interviews/${interviewSlug}/questions/${questionSlug}/solution/`,
    },
    [HTTP_STATUS_CODES.NOT_FOUND],
  );

export const createOrUpdateSubjectiveSolutionAPI = (
  interviewSlug: string,
  payload: ICreateOrUpdateSubjectiveSolutionPayload,
) =>
  // The endpoint returns either `{ solution_slug }` or the full solution
  // (`{ slug }`) — same as the coding create. Use the union so callers extract
  // the slug defensively via `getSolutionSlugFromCreateResponse`.
  sendRequestToInterviewWithCredential<TCreateSolutionAPIResponse>({
    method: HTTP_METHODS.POST,
    url: `/interviews/${interviewSlug}/solutions/`,
    data: payload,
  });

export const updateSubjectiveSolutionAPI = (
  interviewSlug: string,
  solutionSlug: string,
  payload: ICreateOrUpdateSubjectiveSolutionPayload,
) =>
  sendRequestToInterviewWithCredential({
    method: HTTP_METHODS.PUT,
    url: `/interviews/${interviewSlug}/solutions/${solutionSlug}/`,
    data: payload,
  });

export const uploadAttachmentAPI = (
  interviewSlug: string,
  solutionSlug: string,
  file: File,
) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("interview_slug", interviewSlug);
  formData.append("solution_slug", solutionSlug);
  return sendRequestToInterviewWithCredential<IUploadAttachmentAPIResponse>({
    method: HTTP_METHODS.POST,
    url: "/attachments/upload/",
    data: formData,
  });
};

export const deleteAttachmentAPI = (
  interviewSlug: string,
  solutionSlug: string,
  fileName: string,
) =>
  sendRequestToInterviewWithCredential({
    method: HTTP_METHODS.DELETE,
    url: "/attachments/upload/",
    data: {
      interview_slug: interviewSlug,
      solution_slug: solutionSlug,
      file_name: fileName,
    },
  });
